import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} quantityNumber
 * @property {HTMLImageElement} productImage
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * Barra de compra visible desde la inicialización.
 * Utiliza el botón y el formulario originales del producto.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = [
    'stickyBar',
    'addToCartButton',
    'quantityDisplay',
    'quantityNumber',
  ];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  connectedCallback() {
    super.connectedCallback();

    // Permite reinicializar los listeners si el editor reconecta el elemento.
    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartError,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantityUpdate,
      { signal }
    );

    this.#getInitialQuantity();
    this.#refreshTargetButton();
    this.#syncVisibility();

    // Conserva la protección original frente a Shopify Chat.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      this.#syncVisibility();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
      this.#resetTimeout = undefined;
    }

    this.#targetAddToCartButton = null;
  }

  /**
   * Activa el botón original del producto.
   * No selecciona otra variante ni simula cambios de talla.
   */
  handleAddToCartClick = async () => {
    const targetButton = this.#refreshTargetButton();

    if (
      !targetButton ||
      targetButton.disabled ||
      targetButton.getAttribute('aria-disabled') === 'true' ||
      this.refs.addToCartButton.disabled
    ) {
      return;
    }

    targetButton.dataset.puppet = 'true';
    targetButton.click();

    const cartIcon = document.querySelector(
      '.header-actions__cart-icon'
    );

    const stickyButton = this.refs.addToCartButton;
    const productImage = this.refs.productImage;

    stickyButton.dataset.added = 'true';

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }

    // Sin imagen o icono, la compra sigue usando el botón original.
    if (!cartIcon || !productImage) {
      this.#scheduleAddedReset(stickyButton);
      return;
    }

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement('fly-to-cart')
    );

    flyToCartElement.classList.add('fly-to-cart--sticky');

    flyToCartElement.style.setProperty(
      'background-image',
      `url(${productImage.src})`
    );

    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    try {
      await onAnimationEnd([stickyButton, flyToCartElement]);
    } finally {
      if (this.isConnected) {
        this.#scheduleAddedReset(stickyButton);
      }
    }
  };

  /**
   * Restablece la animación del botón.
   * @param {HTMLButtonElement} button
   */
  #scheduleAddedReset(button) {
    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }

    this.#resetTimeout = setTimeout(() => {
      button.removeAttribute('data-added');
      this.#resetTimeout = undefined;
    }, 800);
  }

  /**
   * Actualiza el contenido cuando cambia la variante.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    const { optionValueId } = event.detail ?? {};

    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    const signal = this.#abortController.signal;

    event.promise
      .then(({ detail }) => {
        if (signal.aborted || !detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (
          productId &&
          String(productId) !== this.dataset.productId
        ) {
          return;
        }

        const newStickyAddToCart = html.querySelector(
          'sticky-add-to-cart'
        );

        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector(
          '[ref="stickyBar"]'
        );

        if (!newStickyBar) return;

        const currentStuck = this.#isStuck ? 'true' : 'false';

        const variantAvailable =
          newStickyAddToCart.getAttribute(
            'data-variant-available'
          ) ?? 'false';

        morph(this.refs.stickyBar, newStickyBar, {
          childrenOnly: true,
        });

        this.refs.stickyBar.setAttribute(
          'data-stuck',
          currentStuck
        );

        this.dataset.variantAvailable = variantAvailable;

        if (variant && variant.id) {
          this.dataset.currentVariantId = String(variant.id);
        }

        this.#refreshTargetButton();

        if (variant == null) {
          this.#handleVariantUnavailable();
        }

        this.#updateButtonText();
        this.#syncVisibility();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[sticky-add-to-cart] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Actualiza el título para una combinación no disponible.
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';

    const variantTitleElement = this.querySelector(
      '.sticky-add-to-cart__variant'
    );

    const productId = this.dataset.productId;

    const variantPicker = document.querySelector(
      `variant-picker[data-product-id="${productId}"]`
    );

    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(
      variantPicker.querySelectorAll('input:checked')
    )
      .map(
        (option) =>
          /** @type {HTMLInputElement} */ (option).value
      )
      .filter((value) => value !== '')
      .join(' / ');

    if (!selectedOptions) return;

    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Restablece la marca del botón después de la operación de carrito.
   * @param {
   *   import('@shopify/events').CartLinesUpdateEvent |
   *   import('@shopify/events').CartErrorEvent
   * } event
   */
  #handleCartAddComplete = (event) => {
    const targetButton = this.#targetAddToCartButton;

    const resetPuppet = () => {
      if (targetButton) {
        targetButton.dataset.puppet = 'false';
      }
    };

    if (
      'promise' in event &&
      event.promise instanceof Promise
    ) {
      event.promise.then(resetPuppet, resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Conserva la cantidad seleccionada.
   * @param {
   *   import('@theme/events').QuantitySelectorUpdateEvent
   * } event
   */
  #handleQuantityUpdate = (event) => {
    // Ignora actualizaciones de cantidades del carrito lateral.
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Muestra la barra mediante los estilos nativos.
   */
  #showStickyBar() {
    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
  }

  /**
   * Oculta la barra mediante los estilos nativos.
   */
  #hideStickyBar() {
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
  }

  /**
   * La visibilidad ya no depende del scroll ni del footer.
   * El CSS original sigue ocultando variantes no disponibles.
   */
  #syncVisibility() {
    if (this.#isChatActive()) {
      this.#hideStickyBar();
      return;
    }

    this.#showStickyBar();
  }

  /**
   * Conserva la comprobación original de Shopify Chat.
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;

    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Obtiene el formulario del producto de esta sección.
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    return sectionElement.querySelector(
      `product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Localiza de nuevo el botón original en cada clic.
   * No depende de que haya ocurrido un cambio de talla.
   * @returns {HTMLButtonElement | null}
   */
  #refreshTargetButton() {
    const productForm = this.#getProductForm();

    this.#targetAddToCartButton =
      productForm?.querySelector(
        '[ref="addToCartButton"]'
      ) ?? null;

    return this.#targetAddToCartButton;
  }

  /**
   * Obtiene la cantidad inicial del HTML del producto.
   */
  #getInitialQuantity() {
    this.#currentQuantity =
      parseInt(this.dataset.initialQuantity || '1', 10) || 1;

    this.#updateButtonText();
  }

  /**
   * Actualiza el indicador de cantidad del botón.
   */
  #updateButtonText() {
    const {
      addToCartButton,
      quantityDisplay,
      quantityNumber,
    } = this.refs;

    const available = !addToCartButton.disabled;

    quantityNumber.textContent =
      this.#currentQuantity.toString();

    quantityDisplay.style.display =
      available && this.#currentQuantity > 1
        ? 'inline'
        : 'none';
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    StickyAddToCartComponent
  );
}
